/**
 * Страница зрителя `/watch/<id>` (MADR 0040): тот же клиент, но вместо игры — чужая волна по событиям SSE
 * `/api/live/watch/<id>`. Поле рисуется как в игре, оболочка игры не поднимается: поверх поля своя строка состояния и
 * список команд. Сервер отдаёт страницу только владельцам, поэтому тексты здесь по-русски и без i18n.
 */
import { LiveEvent, TICK_RATE, type TimedCommand } from "@newfungame/contracts";
import { balanceV1 } from "@newfungame/engine";
import { ADMIN_MODE, chibiAtlases, FIRST_ATLASES, loadAtlas } from "../field/atlas";
import { createField } from "../field/field";
import { createPlayback, type PlaybackStatus } from "./playback";

const UI: Record<string, string> = { battle: "бой", boost: "выбирает усиление", hidden: "свёрнуто", window: "окно" };
const COMMAND: Record<TimedCommand["command"]["type"], string> = {
  castSkill: "навык",
  setAutoSkills: "автонавыки",
  chooseBoost: "усиление",
  tapParcel: "посылка",
};
const COMMANDS_SHOWN = 8;
/** Чанк старше — игрок, похоже, ушёл: сервер забывает запись через 3 мин, строка говорит об этом раньше. */
const STALE_MS = 20_000;

function uiLabel(ui: PlaybackStatus["ui"]): string {
  if (!ui) return "";
  return ui.state === "window" && ui.window ? `окно ${ui.window}` : (UI[ui.state] ?? ui.state);
}

function commandLabel(c: TimedCommand): string {
  const sec = (c.tick / TICK_RATE).toFixed(1);
  const cmd = c.command;
  const what =
    cmd.type === "castSkill"
      ? `${COMMAND.castSkill} ${cmd.heroId}`
      : cmd.type === "setAutoSkills"
        ? `${COMMAND.setAutoSkills} ${cmd.enabled ? "вкл" : "выкл"}`
        : cmd.type === "chooseBoost"
          ? `${COMMAND.chooseBoost} ${cmd.boostId}`
          : COMMAND[cmd.type];
  return `${sec} с · ${what}`;
}

export function statusLine(s: PlaybackStatus, now: number, connected: boolean): string {
  const name = s.player?.name ?? "…";
  if (!connected) return `${name} · нет связи с сервером, переподключаюсь`;
  if (s.offline) return `${name} · не в игре (чанков эфира нет)`;
  const ago = s.at === null ? 0 : Math.max(0, Math.round((now - s.at) / 1000));
  const parts = [name, `волна ${s.wave}`, `${Math.floor(s.localTick / TICK_RATE)} с`, uiLabel(s.ui)];
  parts.push(ago >= STALE_MS / 1000 ? `последний чанк ${ago} с назад` : `чанк ${ago} с назад`);
  if (s.remoteTick - s.localTick > 5 * TICK_RATE) parts.push("догоняю");
  return parts.join(" · ");
}

export async function watchMain(playerId: string): Promise<void> {
  const ui = document.getElementById("ui")!;
  ui.replaceChildren();
  const bar = document.createElement("div");
  bar.dataset.testid = "watch-status";
  bar.style.cssText =
    "position:fixed;top:0;left:0;right:0;padding:8px 12px;background:rgba(0,0,0,.6);color:#fff;font:600 14px/1.3 system-ui;z-index:2;pointer-events:none";
  const list = document.createElement("div");
  list.dataset.testid = "watch-commands";
  list.style.cssText =
    "position:fixed;top:40px;right:0;max-width:60vw;padding:8px 12px;color:#fff;font:500 12px/1.4 system-ui;text-align:right;opacity:.85;z-index:2;pointer-events:none;white-space:pre";
  ui.append(bar, list);
  bar.textContent = "…";

  const canvas = document.getElementById("field") as HTMLCanvasElement;
  const atlas = await loadAtlas("/assets/atlas/", ...FIRST_ATLASES);
  const field = await createField(canvas, atlas, { background: "#1a2e12" });
  // как в игре: боссы, затем чиби поверх (поздний атлас перекрывает кадры)
  const boss = loadAtlas("/assets/atlas/", "voxel-boss").then((a) => field.addAtlas(a));
  Promise.all([loadAtlas("/assets/atlas/", ...chibiAtlases(ADMIN_MODE)), boss])
    .then(([chibi]) => field.addAtlas(chibi))
    .catch((e: unknown) => console.warn("watch atlas:", e));

  const playback = createPlayback(balanceV1);
  let connected = false;
  const source = new EventSource(`/api/live/watch/${encodeURIComponent(playerId)}`);
  source.addEventListener("live", (e) => {
    const parsed = LiveEvent.safeParse(JSON.parse((e as MessageEvent<string>).data));
    if (parsed.success) playback.apply(parsed.data, Date.now());
  });
  source.onopen = () => (connected = true);
  source.onerror = () => (connected = false);

  let lastText = "";
  const frame = () => {
    const now = Date.now();
    const snap = playback.advance(now);
    if (snap) field.render(snap, 1);
    const s = playback.status();
    const text = statusLine(s, now, connected);
    if (text !== lastText) {
      bar.textContent = text;
      lastText = text;
    }
    const tail = s.commands.slice(-COMMANDS_SHOWN).map(commandLabel).join("\n");
    if (list.textContent !== tail) list.textContent = tail;
    window.__nfg = { frame: snap?.frame ?? 0, wave: snap?.wave.number ?? 0 };
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
  document.body.dataset.ready = "1";
}

/** Id игрока из адреса `/watch/<uuid>`; иначе `null`. */
export function watchPlayerId(pathname: string): string | null {
  const m = /^\/watch\/([0-9a-f-]{36})$/i.exec(pathname);
  return m ? m[1]! : null;
}
